import { isBootFeatureEnabled } from "owa-metatags";
import { logUsageFromAccounts, logGreyErrorFromAccounts } from "owa-account-analytics";
import { pluggableAccountFunctions } from "./pluggableAccountFunctions";
import { isGccHighOrDod } from "./isSovereignAppEnvironment";
import { getOrigin } from "owa-url/lib/getOrigin";

type SovereignCloudInfraState = {
    status: "Uninitialized";
} | {
    status: "Enabled";
} | {
    status: "Disabled";
} | {
    status: "CheckedBeforeInitialization";
    error: Error;
};

let enablementState: SovereignCloudInfraState = { status: "Uninitialized" };

export function isSovereignCloudInfraEnabled(): boolean {
    if (enablementState.status === "Uninitialized") {
        enablementState = {
            status: "CheckedBeforeInitialization",
            error: new Error("SovereignCloudInfraCheckBeforeInit"),
        };
    }
    return enablementState.status === "Enabled";
}

/**
 * Called during account source list initialization to compute the
 * enablement decision from the boot flight and account state.
 * Keeps the existing decision if it was already set by an early check or via
 * setSovereignCloudInfraDecision on the data worker.
 */
export function initializeSovereignCloudInfra(accountState: {
    hasNoAccounts: boolean;
    allFirstPartyAccountsHaveIdentifiers: boolean;
}): void {
    validateInitOrdering();
    
    if (enablementState.status !== "Uninitialized") {
        return;
    }
    
    const hasBootFlight = isBootFeatureEnabled("acct-sovereignCloudInfraV3");
    const prerequisiteMet = accountState.hasNoAccounts || accountState.allFirstPartyAccountsHaveIdentifiers;
    
    // TEMPORARY WORKAROUND: need to recreate the sovcloud infra flight rollout, but cannot while the existing
    // rollout is active. Hardcoding the flight enabled for GCCH + DoD (where it is already at 100%) so we can
    // stop the existing rollout without breaking Public Preview sovcloud customers and recreate it with ECS V2
    const isHardcodeEnabledSovereignEnvironment = isGccHighOrDod();
    
    const isEnabled = (hasBootFlight || isHardcodeEnabledSovereignEnvironment) && prerequisiteMet;
    enablementState = { status: isEnabled ? "Enabled" : "Disabled" };
    const origin = getOrigin();
    
    logUsageFromAccounts("SovereignCloudInfraDecision", pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.(), {
        hasBootFlight,
        ...accountState,
        isEnabled,
        origin,
        isHardcodeEnabledSovereignEnvironment,
    });
}

/**
 * Called during data worker initialization to set the pre-computed
 * decision from the main thread, ensuring both contexts agree.
 * Once set, initializeSovereignCloudInfra becomes a no-op.
 */
export function setSovereignCloudInfraDecision(enabled: boolean): void {
    validateInitOrdering();
    enablementState = { status: enabled ? "Enabled" : "Disabled" };
}

function validateInitOrdering(): void {
    if (enablementState.status !== "CheckedBeforeInitialization") {
        return;
    }
    
    const { error } = enablementState;
    enablementState = { status: "Disabled" };
    logGreyErrorFromAccounts("SovereignCloudInfraCheckBeforeInit", pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.(), error);
}

/** @internal Test-only. */
export function resetSovereignCloudInfraForTests(): void {
    enablementState = { status: "Uninitialized" };
}
